/**
 * The API's stable, machine-readable error codes.
 *
 * 4xx responses are `{ message, code? }`; `code` is what clients branch on and
 * translate. It lives here — not in `apps/api` — because clients cannot import
 * across app workspaces, and a client that hard-codes this union is exactly
 * how the contract drifts.
 *
 * A client should still tolerate coded-less 4xx bodies: the `X-Api-Key` gate
 * sends `{message:"Unauthorized"}`, the 404 handler sends `{message:"Not Found"}`
 * and Fastify/zod validation failures send a plain message.
 */
export const errorCodes = {
  INVALID_PARAMETER: "INVALID_PARAMETER",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  NOT_FOUND: "NOT_FOUND",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  ACCOUNT_NOT_ACTIVATED: "ACCOUNT_NOT_ACTIVATED",
  ACCOUNT_ARCHIVED: "ACCOUNT_ARCHIVED",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_ALREADY_EXIST: "USER_ALREADY_EXIST",
  USER_ALREADY_ARCHIVED: "USER_ALREADY_ARCHIVED",
  USER_ALREADY_ACTIVATED: "USER_ALREADY_ACTIVATED",
  PASSWORD_RESET_NOT_REQUESTED: "PASSWORD_RESET_NOT_REQUESTED",
  PASSWORD_RESET_EXPIRED: "PASSWORD_RESET_EXPIRED",
  INVALID_CURRENT_PASSWORD: "INVALID_CURRENT_PASSWORD",
  MISSING_REFRESH_TOKEN_IN_COOKIES: "MISSING_REFRESH_TOKEN_IN_COOKIES",
  FORBIDDEN: "FORBIDDEN",
  STUDY_NOT_EDITABLE: "STUDY_NOT_EDITABLE",
  STUDY_NOT_ACTIVE: "STUDY_NOT_ACTIVE",
  STUDY_INVALID_STATUS_TRANSITION: "STUDY_INVALID_STATUS_TRANSITION",
  STUDY_NOT_READY_FOR_ACTIVATION: "STUDY_NOT_READY_FOR_ACTIVATION",
  STUDY_LAST_OWNER: "STUDY_LAST_OWNER",
  OCCURRENCE_NOT_OPEN: "OCCURRENCE_NOT_OPEN",
  SURVEY_ALREADY_TRIGGERED: "SURVEY_ALREADY_TRIGGERED",
  SURVEY_LINK_INVALID: "SURVEY_LINK_INVALID",
  SURVEY_REMINDER_NOT_SENT: "SURVEY_REMINDER_NOT_SENT",
  ANALYSIS_UNAVAILABLE: "ANALYSIS_UNAVAILABLE",
  ANALYSIS_PLUGIN_UNKNOWN: "ANALYSIS_PLUGIN_UNKNOWN",
  SCORED_SURVEY_NOT_IMPORTABLE: "SCORED_SURVEY_NOT_IMPORTABLE",
  PLUGIN_SURVEY_INVALID: "PLUGIN_SURVEY_INVALID",
} as const;

export type ErrorCode = keyof typeof errorCodes;

export const errors = {
  INVALID_PARAMETER: { message: "Invalid parameter.", statusCode: 400 },
  NOT_FOUND: { message: "Not Found.", statusCode: 404 },
  USER_ALREADY_EXIST: { message: "User already exists.", statusCode: 409 },
  USER_ALREADY_ARCHIVED: {
    message: "User is already archived.",
    statusCode: 409,
  },
  USER_ALREADY_ACTIVATED: {
    message: "User account is already activated.",
    statusCode: 409,
  },
  USER_NOT_FOUND: { message: "User not found.", statusCode: 404 },
  PASSWORD_RESET_NOT_REQUESTED: {
    message: "Password reset has not been requested.",
    statusCode: 400,
  },
  PASSWORD_RESET_EXPIRED: {
    message: "Password reset token has expired.",
    statusCode: 400,
  },
  /**
   * 400, NOT 401, deliberately: browser clients treat a 401 as an expired
   * session and try to refresh, so a mistyped current password would sign the
   * user out of a form they are standing in.
   */
  INVALID_CURRENT_PASSWORD: {
    message: "Current password is incorrect.",
    statusCode: 400,
  },
  INVALID_CREDENTIALS: {
    message: "Incorrect email or password",
    statusCode: 401,
  },
  MISSING_REFRESH_TOKEN_IN_COOKIES: {
    message: "Missing refreshToken in cookies",
    statusCode: 400,
  },
  ACCOUNT_LOCKED: {
    message:
      "Account locked after too many failed login attempts. Reset your password to unlock it.",
    statusCode: 403,
  },
  ACCOUNT_NOT_ACTIVATED: {
    message: "Account has not been activated",
    statusCode: 403,
  },
  ACCOUNT_ARCHIVED: {
    message: "Account has been archived",
    statusCode: 403,
  },
  FORBIDDEN: { message: "Forbidden", statusCode: 403 },
  /**
   * A study is only editable in DRAFT. Covers PATCH /studies/:id, the three
   * survey-attachment endpoints and DELETE /studies/:id.
   *
   * 409 rather than 403: the caller is allowed to do this, the resource state
   * is what refuses. All four study codes below are 409 for the same reason.
   */
  STUDY_NOT_EDITABLE: {
    message: "This study is no longer editable.",
    statusCode: 409,
  },
  /**
   * Consent and answer submission need an ACTIVE study; enrolment only needs a
   * study that is not CLOSED.
   */
  STUDY_NOT_ACTIVE: {
    message:
      "This study is not open to that action: a draft accepts no consent or answers, and a closed study accepts nothing further.",
    statusCode: 409,
  },
  STUDY_INVALID_STATUS_TRANSITION: {
    message: "This study status change is not allowed.",
    statusCode: 409,
  },
  STUDY_NOT_READY_FOR_ACTIVATION: {
    message:
      "This study cannot be activated yet. Everything freezes on activation, so it first needs at least one survey, and a title and a consent form in each of its declared languages.",
    statusCode: 409,
  },
  /**
   * A study must always have someone who can steward it, so the last active
   * OWNER cannot be removed or demoted. Hand over first: promote the next
   * steward, then step down. 409 rather than 403 — the caller is allowed to
   * manage members, it is the resulting state that is refused.
   */
  STUDY_LAST_OWNER: {
    message:
      "This study would be left with no owner. Promote another owner before removing or demoting this one.",
    statusCode: 409,
  },
  /**
   * The occurrence named by the submission is not answerable: it has not
   * opened yet, or its window has closed. The window closes hard by design,
   * so this is the normal end of a missed occurrence rather than an edge case
   * — a late answer to a symptom questionnaire carries recall bias and is
   * worth less to a study than a recorded miss.
   */
  OCCURRENCE_NOT_OPEN: {
    message: "This survey is not open for answers right now.",
    statusCode: 409,
  },
  /**
   * Two reasons, one code: a recurring manual schedule has already been
   * started (a second trigger would run two overlapping series), or an
   * occurrence is still open (two answerable copies of one survey would leave
   * the patient unable to say which they are answering). A one-shot can be
   * fired again as soon as the last one is answered or expired.
   */
  SURVEY_ALREADY_TRIGGERED: {
    message: "This survey is already running for this patient.",
    statusCode: 409,
  },
  /**
   * The magic link texted to a patient cannot be exchanged: unknown, already
   * scrubbed, or past its TTL. One code for all three deliberately — telling
   * "never existed" from "expired" apart is an oracle, and neither changes what
   * the patient does, which is to fall back to the OTP login. A link whose
   * OCCURRENCE has closed or been answered is a different case and answers
   * OCCURRENCE_NOT_OPEN, because that one has a useful thing to say.
   */
  SURVEY_LINK_INVALID: {
    message: "This link is no longer valid.",
    statusCode: 401,
  },
  /**
   * A clinician asked for a reminder to be texted again and it did not go out:
   * the deployment sends no reminders, the patient has no phone number, or the
   * provider refused.
   *
   * ONE code for all three, and the message says which. What the clinician does
   * next differs (check the number, ask an administrator, try again), but what
   * they must not do is assume the patient was texted, and that is the whole
   * content of this answer. It is also why this route reports a failure at all
   * where the sweep and the trigger route swallow one: there, the SMS rides
   * along with a write that already succeeded; here the SMS IS the action.
   */
  SURVEY_REMINDER_NOT_SENT: {
    message: "The reminder could not be sent.",
    statusCode: 502,
  },
  /**
   * The analysis service is unreachable, timed out, or answered with something
   * that failed contract validation. A 503 rather than a 500 because it is a
   * dependency being down, not a bug here, and the client should offer a retry
   * rather than an apology. Nothing is cached on this path, so a bad deploy
   * cannot poison stored results.
   */
  ANALYSIS_UNAVAILABLE: {
    message: "Analysis is temporarily unavailable.",
    statusCode: 503,
  },
  /** Installing a plugin package this deployment does not ship. */
  ANALYSIS_PLUGIN_UNKNOWN: {
    message: "No analysis plugin by that name is available on this deployment.",
    statusCode: 409,
  },
  /**
   * An uploaded questionnaire declares `metadata.analysis.plugin`.
   *
   * Scored questionnaires arrive inside a plugin package, where the scoring and
   * the questions are versioned together and validated against each other at
   * boot. A file claiming a binding has no such guarantee, and honouring it
   * would let a hand-edited upload be scored by code written for a different
   * set of questions. Install it from Settings instead.
   */
  SCORED_SURVEY_NOT_IMPORTABLE: {
    message:
      "This questionnaire declares a scoring plugin. Scored questionnaires are installed from the settings page, not uploaded.",
    statusCode: 409,
  },
  /**
   * A plugin package ships a `survey.json` this API cannot read.
   *
   * Deliberately NOT ANALYSIS_UNAVAILABLE: that says "the scoring service is
   * down, try again", and retrying a malformed questionnaire will never work.
   * The failing field paths travel in the message, because the person who has
   * to act is whoever authored the package and they cannot read the API's log.
   * `bun run schemas:emit` publishes the schema they should have validated
   * against, and the Python suite checks every packaged questionnaire with it.
   */
  PLUGIN_SURVEY_INVALID: {
    message: "This plugin's questionnaire does not match the survey import format.",
    statusCode: 422,
  },
} as const satisfies Record<ErrorCode, { message: string; statusCode: number }>;
